/**
 * 「浏览与编辑」页筛选区的**选项来源** + **"到底选没选"的判定** + **本机记忆**
 * （纯函数，便于单测）。
 *
 * ## 为什么年级选项要单独放这里（2026-10-17 修的一个真 bug）
 *
 * 改造前「年级」下拉的选项是**从课文列表里推出来的**：
 *
 *     [...new Set(lessons.map((lesson) => lesson.gradeCode))].map(...)
 *
 * 也就是说 —— **没有课文就没有年级选项**。用户这次导入的是 280 个生字，
 * 按「按书录入 → 识字表」录的，**一节课都没有**（`lesson` 表 0 行），
 * 于是那个下拉里只剩「全部年级」一项，12 个年级全都不见了。
 * 而「按书录入」那页的年级是好用的 —— 它用的是 `GRADES` 常量，
 * 正好反证"常量在、是这个筛选的填充逻辑错了"。
 *
 * ⚠️ 所以这里的口径是：
 *   · 年级选项 = **项目里既有的 `GRADES` 全量**（12 个），**不**按"有没有数据"过滤 ——
 *     只列出"确实有数据的年级"会让用户以为数据丢了（这正是本次要避免的错觉）；
 *   · 「全部年级」是**占位项**（`value=''` = 不筛选），永远排在第一；
 *   · 选项**不依赖任何接口数据**（课本列表 / 课文列表拿不到时，这个下拉照样完整）。
 *
 * ## 2026-10-18：筛选全都没选时**不列字词**（本轮用户要求）
 *
 * 用户原话：
 *
 * > 这里没有选择任何选项，下面就不应该出现字或词的列表。
 *
 * 现场：五个下拉全停在「全部」，下面直接列出 280 个生字（当前显示 200）。
 * 于是这个文件同时负责三件**同一套口径**的事（组件只调用，不各写一份）：
 *
 *   1. `isFilterActive()` —— 什么算"用户做了选择"（逐项见下面那张表）；
 *   2. `browseListsEnabled()` —— 三个列表查询的 `enabled` 判据：
 *      **未选就一个请求都不发**（不是"先拉 200 条再藏起来"：慢网下会闪一下，
 *      而且 280 条白拉）；
 *   3. `readBrowseFilter()` / `writeBrowseFilter()` —— 上次的选择记在**本机**
 *      （`localStorage`，不上传、不入库），恢复时逐项校验，失效的值安全回落
 *      （`reconcileBrowseFilter()` 负责"那本书已经被删了"这一类）。
 *
 * ## 2026-10-18（本轮）：改成「**先选、再查询**」（用户要求）
 *
 * 用户原话：
 *
 * > 这里最好设置一个查询按钮，选好所有条件后，点查询按钮才出结果，
 * > **不要选一个条件就刷新一个**。
 *
 * 于是筛选分成**两套状态**（本文件只给判据，状态本身在页面里）：
 *
 * | 名字 | 谁在用 | 什么时候变 |
 * | --- | --- | --- |
 * | **待生效**（`draft`） | 下拉 / 勾选框的当前值 + 「查询」按钮的判据 | 用户一动就变，**不发任何请求** |
 * | **已生效**（`applied`） | 三个列表查询的参数与 `enabled`，本机记忆存的也是它 | **只有点「查询」**（或恢复出上次那一套）时变 |
 *
 * 由此定下这几条口径（下面每个函数/常量都对应其中一条）：
 *
 *   · `browseQueryDecision()` —— 点「查询」时到底发生什么（没条件就不许查）；
 *   · `hasPendingBrowseChanges()` / `describeBrowseQuery()` —— 有没有"改了还没查"的
 *     改动，以及界面上那句提示（否则用户会以为卡住了）；
 *   · `isBrowseQueryShortcut()` —— 回车也算「查询」（桌面端）；
 *   · 重置只清**待生效**那套，绝不顺手查询（`onReset` 那一侧只 set 待生效状态）；
 *   · **没有可查条件 ⇒ 按钮禁用**（`describeBrowseQuery().canQuery`）；
 *   · 未选条件、也从没查过 ⇒ 不请求、**页面上只剩筛选卡**（`isFilterActive`）。
 *
 * ## 2026-10-18（本轮）：删掉两块界面（用户圈红，"都不需要"）
 *
 * 用户圈的是「浏览与编辑 → 筛选」里两块东西，都**整块删掉、不留替代品**：
 *
 *   1. 查询条左侧那句禁用原因（原话是"要先把条件选上，否则一进来就会把整站
 *      几百个字全拉下来"那个意思）—— 「查询」按钮**仍然**在没有条件时禁用，
 *      但**不再解释为什么**（也不准换成别的解释句）；
 *   2. 未选时的那张引导卡（标题 + 正文 + 三个快捷按钮）—— 连
 *      `describeBrowseNeedsSelection()` / `quickBrowseChips()` 一起删掉：
 *      留着这两个纯函数，下一轮很容易又被接回界面去。
 *
 * ⚠️ 所以"没选条件"时页面上**只剩筛选卡**（含灰掉的「查询」按钮）——
 *    这是用户要的效果，**不要**再补一句提示。
 * ⚠️ 删快捷按钮后"点一下就能选好"这个便利没有了 —— 用户明确要求，照做。
 *
 * ## 「什么算选过了」逐项口径
 *
 * | 筛选项 | 字段 | 算不算"选择" | 理由 |
 * | --- | --- | --- | --- |
 * | 出版社 | `publisher` | **算**（但**不是必要条件**） | 与年级 / 学期组合起来就是"哪一册"；单独用 = 这家出版社的所有册 |
 * | 年级 | `gradeCode` | 算 | 12 个年级，「全部」以外的取值都是明确缩小 |
 * | 学期 | `semester` | 算 | 上册 / 下册 / 不分 |
 * | 课文 | `lessonId` | 算 | 课文界面当前隐藏（`DICTATION_LESSONS_UI_ENABLED=false`），但字段口径不变 |
 * | 在哪张表上 | `listType` | 算 | 识字表 / 写字表 / 词语表 |
 * | 词语类别 | `wordCategory` | 算 | 常用词 / 词语 / 成语 |
 * | 只看人工改过拼音的 | `overriddenOnly` | 算 | 勾上就是一个明确的收窄（纠音清单） |
 * | 只看多音字（按你标注的） | `polyphonicOnly` | 算 | 同上（判据是管理员在字的行上做的标注，不是拼音库自动判断） |
 * | 只看课本词语表里的词 | `fromTextbookOnly` | 算 | 同上 |
 * | 搜索词 | —— | **本页没有搜索框** | 只有 `fetchListItems`（按「书 + 表」看）才有 `keyword`，这一屏没有这一项，所以不存在"搜索词非空" |
 *
 * ⚠️ 为什么「出版社」算选择、却**不**是必要条件（用户只有 1 本课本）：
 *   · 要求先选出版社 ⇒ 每次进来都要点一下「人民教育出版社」，
 *     而它和默认的「全部出版社」返回的是同一批字 —— 纯摩擦；
 *   · 「全部出版社」本身是一个**合法且完整**的视图（不筛选），
 *     而年级 / 学期 / 表 / 开关任意一项都能把范围收窄到人能看下去的量；
 *   · 所以口径统一为：**任何一项离开默认值就算选过了**，其中出版社是最不必要的那一项。
 *
 * ## 2026-10-18（本轮）：筛选里的「课本版本」→「出版社」
 *
 * 用户原话：
 *
 * > 不要把"出版社""年级""学期"和"表"都集中在"课本版本"一个下拉框中，因为右边
 * > 已经分别有"年级""学期"和"在哪张表上"了。因此，你把"课本版本"改成"出版社"，
 * > 下拉菜单也只显示"出版社"。
 *
 * 所以这里曾经有的 `textbookId` **换成了** `publisher`（一个字段替一个字段）：
 *   · 下拉选项 = 课本列表里**去重后的出版社**（`publisherFilterOptions`）；
 *   · 查询语义 = 出版社 + 年级 + 学期（后端 `dictationListQuerySchema.publisher`），
 *     三者组合唯一确定一册；出版社为空 = 不按出版社筛（跨出版社）；
 *   · 「新增生字 / 新增词语」的落点**必须**是一册：用 `findFilterVolume()`
 *     解析（出版社 + 年级 + 学期），解析不到就禁用并说明原因（`describeCreateTargetGap`）；
 *   · 本机记忆里的旧值（`textbookId`）**安全回落**：读不出来就当没选（见
 *     `parseStoredBrowseFilter`），不报错、也不去想"那本书还在不在"。
 */

import {
  GRADE_CODES,
  GRADES,
  gradeLabel,
  publisherDisplayNameOf,
  publisherRefKeyOf,
  textbookRefKey,
} from '@fht/shared';
import { semesterLabel } from './api/dictation';

/** 「全部年级」占位项（`value=''` = 不按年级筛选）。 */
export const ALL_GRADES_OPTION = { value: '', label: '全部年级' } as const;

/**
 * **真实**年级选项：`GRADES` 全量（12 个），**不含**「全部年级」占位项。
 *
 * 顺序 = `GRADES` 的顺序（小学一年级 → 高中三年级），不做任何过滤。
 */
export function gradeFilterOptions(): { value: string; label: string }[] {
  return GRADES.map((grade) => ({ value: grade.code, label: grade.label }));
}

/**
 * 下拉里**真正渲染**的选项：「全部年级」在最前面，后面跟着 12 个年级。
 *
 * ⚠️ 不要在调用处再拼一次占位项 —— 那样很容易拼漏 / 拼重（本次的教训）。
 */
export function gradeSelectOptions(): { value: string; label: string }[] {
  return [{ ...ALL_GRADES_OPTION }, ...gradeFilterOptions()];
}

// ---------------------------------------------------------------- 出版社（2026-10-18）

/** 「全部出版社」占位项（`value=''` = 不按出版社筛选）。 */
export const ALL_PUBLISHERS_OPTION = { value: '', label: '全部出版社' } as const;

/**
 * 下拉里的**真实**出版社选项 = 课本列表里**去重后的出版社**（不含占位项）。
 *
 * 口径（2026-10-18 修 bug 后）：
 *   · 显示**人话**（`publisherDisplayNameOf`）：历史行 `publisher = pub_xxxxxxxx` /
 *     `name = 人民教育出版社` 显示成人话；⛔ 下拉里**绝不出现 `pub_` 代号**
 *     （那正是用户截图里那串"PUB 后面很多字母和数字"）；
 *   · 去重按**出版社身份键**（`publisherRefKeyOf`：空白 / 大小写不算区别）——
 *     历史代号行与导入人话行是**同一家**，只留一项（以前按原样字符串去重 ⇒ 两项）；
 *   · 选项的 `value` 就是这串人话：查询串发的是它，服务端按
 *     `publisher` **或**（历史代号行的）`name` 匹配，所以两册的数据都查得到；
 *   · 空串出版社不列（那不是"一个出版社"）；
 *   · 课本列表拿不到（`null`）→ 返回空数组（界面只剩「全部出版社」，
 *     并另有一句"没拿到课本列表"的说明，见 `DictationItemsPanel`）。
 */
export function publisherFilterOptions(
  textbooks: readonly { publisher: string; name?: string }[] | null | undefined,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const textbook of textbooks ?? []) {
    const label = publisherDisplayNameOf(textbook);
    const key = textbookRefKey(label);
    if (label === '' || key === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/** 下拉里**真正渲染**的选项：「全部出版社」在最前面，后面跟着去重后的出版社。 */
export function publisherSelectOptions(
  textbooks: readonly { publisher: string; name?: string }[] | null | undefined,
): { value: string; label: string }[] {
  return [
    { ...ALL_PUBLISHERS_OPTION },
    ...publisherFilterOptions(textbooks).map((publisher) => ({ value: publisher, label: publisher })),
  ];
}

/** 「出版社 + 年级 + 学期」三格齐了没有（齐了才可能唯一确定一册）。 */
export function isFilterVolumeComplete(
  filter: Pick<BrowseFilterState, 'publisher' | 'gradeCode' | 'semester'>,
): boolean {
  return filter.publisher !== '' && filter.gradeCode !== '' && filter.semester !== '';
}

/** 一本书里判"是不是这一册"够用的那几个字段（`TextbookView` 的结构子集）。 */
export interface FilterTextbookLike {
  id: string;
  publisher: string;
  /** 展示名：历史行的出版社人话可能只在这里（`publisher` 是 `pub_…` 代号时）。 */
  name?: string;
  gradeCode: string;
  semester: string;
}

/**
 * **这一册到底是哪一本**（出版社 + 年级 + 学期 → 课本）。
 *
 * ⚠️ 一本书 = 一个具体的册（`uq_textbook_identity`），所以三者齐了就是**一本**：
 *    这个函数只回答"那一本在不在" —— 解析不到就是 `null`（不是"没有筛选"）。
 * ⚠️ 解析不到有两个原因，界面上要分开说：
 *    · 三格没齐（`publisher` / `gradeCode` / `semester` 有空）→ 根本还没指定一册；
 *    · 三格齐了但列表里没有这一册 → "人民教育出版社 · 小学一年级 · 下册 还没有课本"。
 *    → `describeCreateTargetGap()` 负责把两者说成人话。
 * ⚠️ 课本列表拿不到（`null`）也返回 `null`：**不要**据此说"没有这一册"。
 * ⚠️ 出版社按**身份键**比（`publisherRefKeyOf`），不是原样字符串比：
 *    历史行的 `publisher` 是 `pub_be07fb11`，直接比字符串会永远解析不到那一册。
 */
export function findFilterVolume<T extends FilterTextbookLike>(
  textbooks: readonly T[] | null | undefined,
  filter: Pick<BrowseFilterState, 'publisher' | 'gradeCode' | 'semester'>,
): T | null {
  if (!isFilterVolumeComplete(filter)) return null;
  const wanted = textbookRefKey(filter.publisher);
  if (wanted === '') return null;
  const match = (textbooks ?? []).find(
    (textbook) =>
      publisherRefKeyOf(textbook) === wanted &&
      textbook.gradeCode === filter.gradeCode &&
      textbook.semester === filter.semester,
  );
  return match ?? null;
}

/** 一册的界面叫法（「人民教育出版社 · 小学一年级 · 上册」）。 */
export function filterVolumeLabel(
  filter: Pick<BrowseFilterState, 'publisher' | 'gradeCode' | 'semester'>,
): string {
  return [
    filter.publisher,
    filter.gradeCode === '' ? '' : gradeLabel(filter.gradeCode),
    filter.semester === '' ? '' : semesterLabel(filter.semester),
  ]
    .filter((part) => part !== '')
    .join(' · ');
}

/**
 * 「出版社 + 年级 + 学期」这一册**还没有课本**时的那句话（空串 = 不该说）。
 *
 * 用户要求："如果组合没有对应的册 → 列表空 + 一句人话（例如
 * 「人民教育出版社 · 小学一年级 · 下册 还没有课本」），**不要报错**"。
 *
 * 只在这几种情况下返回空串（也就是说"不该说"）：
 *   · 三格没齐（还没指定一册）；
 *   · 这一册**存在**；
 *   · 课本列表**没拿到**（`null`）—— 那时说"没有"是在编。
 */
export function describeFilterVolumeGap(
  textbooks: readonly FilterTextbookLike[] | null | undefined,
  filter: Pick<BrowseFilterState, 'publisher' | 'gradeCode' | 'semester'>,
): string {
  if (textbooks === null || textbooks === undefined) return '';
  if (!isFilterVolumeComplete(filter)) return '';
  if (findFilterVolume(textbooks, filter) !== null) return '';
  return `${filterVolumeLabel(filter)} 还没有课本`;
}

/**
 * 「新增生字 / 新增词语」为什么点不了（空串 = 能点，落点就是 `findFilterVolume()` 那本）。
 *
 * 顺序就是界面上的引导顺序：先等列表 → 选出版社 → 选年级 → 选学期 → 这一册存不存在。
 * ⚠️ 不返回"请先选一个课本版本"那种笼统话：用户看到的是「出版社」那一格。
 */
export function describeCreateTargetGap(
  textbooks: readonly FilterTextbookLike[] | null | undefined,
  filter: Pick<BrowseFilterState, 'publisher' | 'gradeCode' | 'semester'>,
): string {
  if (textbooks === null || textbooks === undefined) return '课本列表还没拿到 —— 等它回来再新增';
  if (filter.publisher === '') return '请先选「出版社」：新增要写进具体的一册';
  if (filter.gradeCode === '') return '再选「年级」：出版社 + 年级 + 学期 才能确定一册';
  if (filter.semester === '') return '再选「学期」：出版社 + 年级 + 学期 才能确定一册';
  return describeFilterVolumeGap(textbooks, filter);
}

// ---------------------------------------------------------------- 筛选状态

/**
 * 「浏览与编辑」的筛选状态（页面持有；`DictationItemsPanel` 只读）。
 *
 * ⚠️ 空串 = 「全部」= 不筛选。`lessonId` 现在不渲染（课文界面隐藏），
 *    但字段留着 —— 开关打开时一个字都不用改（见 `lib/dictationLessonsUi.ts`）。
 */
export interface BrowseFilterState {
  /**
   * **出版社**（空串 = 「全部出版社」= 不按出版社筛）。
   *
   * ⚠️ 2026-10-18：这个字段**取代**了原来的 `textbookId`。一本课本 = 一个具体的册，
   *    所以"出版社 + 年级 + 学期"本来就能唯一确定那一册；把 `textbookId` 留在
   *    下拉里只会把出版社 / 年级 / 学期 / 表 / 统计全塞进一个选项（用户圈红的那个）。
   *    ⛔ 不要为了"再选一本具体的书"把它加回来：那是「按书录入」那页的事。
   */
  publisher: string;
  gradeCode: string;
  semester: string;
  lessonId: string;
  /** 表类型：`shizi` 识字表 / `xiezi` 写字表 / `cibiao` 词语表；空串 = 全部表。 */
  listType: string;
  wordCategory: string;
  fromTextbookOnly: boolean;
  overriddenOnly: boolean;
  /**
   * 只看多音字（**按管理员在字的行上的标注**）。
   *
   * ⚠️ 不是"拼音库里有几个读音"：那个口径把变调/古音/生僻音都算进来，
   *    280 个一年级生字会自动标出 134 个。没标注的一律按单音字处理。
   */
  polyphonicOnly: boolean;
}

/** 全默认 = 「全部」+ 三个开关都没勾（**正常初始状态**，不是错误态）。 */
export const EMPTY_BROWSE_FILTER: BrowseFilterState = {
  publisher: '',
  gradeCode: '',
  semester: '',
  lessonId: '',
  listType: '',
  wordCategory: '',
  fromTextbookOnly: false,
  overriddenOnly: false,
  polyphonicOnly: false,
};

export type BrowseFilterField = keyof BrowseFilterState;

/** 学期下拉的有效取值（空串 = 「全部学期」）。 */
export const SEMESTER_VALUES = ['上', '下', '不分'] as const;
/**
 * 「在哪张表上」下拉的有效取值（空串 = 「全部表」）。
 *
 * ⚠️ 2026-10-19：补上 `cibiao`（词语表）。取值与后端 `LIST_TYPES` **逐字一致**
 *    （`shared/dictation/listing.ts`），没有新造枚举。
 */
export const LIST_TYPE_VALUES = ['shizi', 'xiezi', 'cibiao'] as const;
/** 词语类别下拉的有效取值（空串 = 「词的全部类别」）。 */
export const WORD_CATEGORY_VALUES = ['common', 'phrase', 'idiom'] as const;

/** 字段 → 界面上的叫法（提示语里要能对上用户看到的那个下拉/勾选框）。 */
const FIELD_LABELS: Record<BrowseFilterField, string> = {
  publisher: '出版社',
  gradeCode: '年级',
  semester: '学期',
  lessonId: '课文',
  listType: '在哪张表上',
  wordCategory: '词语类别',
  fromTextbookOnly: '只看课本词语表里的词',
  overriddenOnly: '只看人工改过拼音的',
  polyphonicOnly: '只看多音字（按你标注的）',
};

/**
 * **有没有"选过"** —— 只回答这一个问题（`true` = 应该列字词）。
 *
 * 口径：**任意一项**离开默认值就算选过了。空串 / `false` 都是"全部"。
 * ⚠️ 不要在这里加"课本必须选"：那会让只有一本课本的用户每次都要多点一下
 *    （理由见文件头的表）。
 */
export function isFilterActive(filter: BrowseFilterState): boolean {
  return (
    filter.publisher !== '' ||
    filter.gradeCode !== '' ||
    filter.semester !== '' ||
    filter.lessonId !== '' ||
    filter.listType !== '' ||
    filter.wordCategory !== '' ||
    filter.fromTextbookOnly ||
    filter.overriddenOnly ||
    filter.polyphonicOnly
  );
}

/** 哪几项是"选过"的（按上表顺序）—— 界面用来说明"为什么下面出东西了"。 */
export function activeFilterFields(filter: BrowseFilterState): BrowseFilterField[] {
  const fields: BrowseFilterField[] = [];
  if (filter.publisher !== '') fields.push('publisher');
  if (filter.gradeCode !== '') fields.push('gradeCode');
  if (filter.semester !== '') fields.push('semester');
  if (filter.lessonId !== '') fields.push('lessonId');
  if (filter.listType !== '') fields.push('listType');
  if (filter.wordCategory !== '') fields.push('wordCategory');
  if (filter.fromTextbookOnly) fields.push('fromTextbookOnly');
  if (filter.overriddenOnly) fields.push('overriddenOnly');
  if (filter.polyphonicOnly) fields.push('polyphonicOnly');
  return fields;
}

/**
 * 三个列表查询要不要发（`useQuery({ enabled })` 的唯一判据）。
 *
 * 两个条件：
 *   1. `isFilterActive()` —— **未选就一个请求都不发**（这是本轮的核心要求）；
 *   2. 恢复的筛选里带了出版社时，先等课本列表到手再发：
 *      那个出版社可能已经没有课本了（见 `reconcileBrowseFilter`），
 *      没校对就发＝白拉一次 + 慢网下闪一下"0 条"。
 *
 * ⚠️ 不依赖课本列表的那一支（只选了年级/学期/表/开关）**不等**任何东西 ——
 *    打开页面就该出结果，多等一个来回是没有意义的。
 */
export function browseListsEnabled(
  filter: BrowseFilterState,
  context: { textbooksSettled: boolean },
): boolean {
  if (!isFilterActive(filter)) return false;
  if (filter.publisher !== '' && !context.textbooksSettled) return false;
  return true;
}

// ---------------------------------------------------------------- 「按表分块」

/** 这一屏要渲染哪一块（生字 / 词语）。**不渲染** = 连卡片都不进 DOM。 */
export interface BrowseBlocks {
  hanzi: boolean;
  words: boolean;
}

/**
 * 按「在哪张表上」决定**渲染哪一块**（2026-10-19 用户要求）。
 *
 * 用户原话（验收标准）：
 *   · 识字表 / 写字表 ⇒ **只显示字**（"只有字的时候就显示「字」这一块，
 *     「词语（含成语）」那块不显示"）；
 *   · 词语表 ⇒ **只显示词语**（"查词语表时就只显示「词语」，生字那块不显示"）；
 *   · 全部表（或不筛）⇒ 两块都显示（保持原样）。
 *
 * ⚠️ 口径是**不渲染**，不是 CSS 隐藏：卡片、计数、"新增生字 / 新增词语"按钮
 *    一起不进 DOM；调用处还会据此**关掉另一块的请求**（`enabled`），
 *    所以也不会白拉一份数据。
 * ⚠️ 判据吃的是**已生效**那套筛选（`appliedFilter`）：用户在下拉里刚点「词语表」
 *    但还没点「查询」时，下面还是上一次查询的结果 —— 与列表的其它部分同一口径。
 * ⚠️ 认不出来的值（老 localStorage / 手改的）按「全部表」处理：
 *    `dictationFilters` 读盘时已经把它回落成空串，这里再兜一层不至于两块都不渲染。
 */
export function browseBlocks(
  filter: Pick<BrowseFilterState, 'listType'>,
): BrowseBlocks {
  if (filter.listType === 'cibiao') return { hanzi: false, words: true };
  if (filter.listType === 'shizi' || filter.listType === 'xiezi') {
    return { hanzi: true, words: false };
  }
  return { hanzi: true, words: true };
}

// ---------------------------------------------------------------- 「先选、再查询」

/**
 * 两套筛选（待生效 / 已生效）是否**逐项相同**。
 *
 * ⚠️ 逐项比（不用 `JSON.stringify`）：字段顺序变了、多了字段都会让后者误判"有改动"，
 *    而那会让「有改动」的提示一直挂着。
 */
export function sameBrowseFilter(a: BrowseFilterState, b: BrowseFilterState): boolean {
  return (
    a.publisher === b.publisher &&
    a.gradeCode === b.gradeCode &&
    a.semester === b.semester &&
    a.lessonId === b.lessonId &&
    a.listType === b.listType &&
    a.wordCategory === b.wordCategory &&
    a.fromTextbookOnly === b.fromTextbookOnly &&
    a.overriddenOnly === b.overriddenOnly &&
    a.polyphonicOnly === b.polyphonicOnly
  );
}

/**
 * 有没有"改了但还没点查询"的改动（决定要不要亮那句提示与按钮上的小圆点）。
 *
 * ⚠️ 这个值**只影响提示**，不参与任何查询的 `enabled`：
 *    待生效的改动一律不下发（用户原话"不要选一个条件就刷新一个"）。
 */
export function hasPendingBrowseChanges(
  draft: BrowseFilterState,
  applied: BrowseFilterState,
): boolean {
  return !sameBrowseFilter(draft, applied);
}

/**
 * 这个按键算不算「查询」（桌面端回车）。
 *
 * 只认 `key === 'Enter'`（小键盘的回车在浏览器里也叫 `Enter`）。
 * ⚠️ 不看修饰键：筛选区里**没有文本输入框**，Shift / Ctrl + Enter 没有任何别的含义，
 *    为它多一条分支只会多一种"按了没反应"的可能。
 */
export function isBrowseQueryShortcut(key: string): boolean {
  return key === 'Enter';
}

/** 查询条上的文案与状态（一个函数说完，界面不各写一份）。 */
export interface BrowseQueryState {
  /** 「查询」按钮能不能点：**没有任何可查条件时禁用**。 */
  canQuery: boolean;
  /** 有未生效的改动（按钮亮小圆点 + 琥珀色提示）。 */
  pending: boolean;
  /** 按钮旁边那行提示（空串 = 什么都不说）。 */
  hint: string;
  /** 按钮的 `title`（鼠标悬停也能看到原因）。 */
  title: string;
}

/**
 * ⛔ **这里原来有一句"没选任何条件时的禁用原因"**（就是用户圈红删掉的那句），
 *    2026-10-18 本轮按用户要求**整句删除**：按钮灰着就够了，旁边不再解释为什么。
 *    不要把它换一种说法加回来（用户圈红的就是那句话本身）。
 */

/** 有未生效改动时的提示（用户看不到它就会以为界面卡住了）。 */
export const BROWSE_QUERY_PENDING_HINT = '有改动，点「查询」才会生效';

/** 已经查过、但用户把待生效那套清空了的提示（列表此时**还是上一次的结果**）。 */
export const BROWSE_QUERY_RESET_HINT = '待生效的条件已清空；列表还是上一次查询的结果。';

/**
 * 查询条的状态与提示。
 *
 * 四种情况（顺序就是取舍顺序）：
 *   1. 待生效那套没有任何条件 → 禁用，**不解释为什么**（`hint` / `title` 都是空串；
 *      唯一留下的那句是"列表还是上一次的结果"，它说的是**列表**不是按钮）；
 *   2. 有待生效的改动 → 可用 + 琥珀色"点了才生效"；
 *   3. 已是当前生效的那套 → 可用 + 不多话（提示是空串）；
 *   4. 其余（首次进入、什么都没选）→ 同 1。
 */
export function describeBrowseQuery(
  draft: BrowseFilterState,
  applied: BrowseFilterState,
): BrowseQueryState {
  const canQuery = isFilterActive(draft);
  /**
   * ⚠️ `pending` 只在**可查**时才算：清空之后按钮是禁用的，
   *    那时在灰按钮上亮一个小圆点等于叫人去点一个点不动的东西。
   */
  const pending = canQuery && hasPendingBrowseChanges(draft, applied);
  if (!canQuery) {
    return {
      canQuery: false,
      pending,
      /**
       * ⚠️ 2026-10-18（本轮）：**不再说"为什么禁用"**（用户圈红的那句已整句删除）。
       *    只留"待生效已清空、列表还是上一次的结果"这一句 —— 它讲的是列表状态：
       *    条件清空之后列表**没有**跟着变，不说用户会以为界面卡住了。
       * ⛔ 不要在这里补一句"要先选条件"之类的解释（等于没删）。
       */
      hint: isFilterActive(applied) ? BROWSE_QUERY_RESET_HINT : '',
      title: '',
    };
  }
  if (pending) {
    return {
      canQuery: true,
      pending: true,
      hint: BROWSE_QUERY_PENDING_HINT,
      title: BROWSE_QUERY_PENDING_HINT,
    };
  }
  return { canQuery: true, pending: false, hint: '', title: '按当前条件再查一次' };
}

/** 点「查询」的裁决（纯函数：界面照着做，测试照着断言）。 */
export interface BrowseQueryDecision {
  /** 允不允许查（没有可查条件时 `false`，界面据此禁用按钮）。 */
  allowed: boolean;
  /** 允许时"已生效"要变成的那一套（**新对象**，调用处直接 set）。 */
  next: BrowseFilterState;
  /**
   * 这次点下去会不会**真的改变**已生效那套。
   *
   * ⚠️ 不允许时恒为 `false`（"不让查"就等于"什么都不变"）；
   *    `false` + `allowed` = 条件没变，语义是"再查一次"（refetch）。
   */
  changed: boolean;
}

/**
 * 点「查询」= 把**待生效**那套升成**已生效**（只有这一步会下发请求）。
 *
 * ⚠️ 不允许查时 `next` 原样返回已生效那套（不清空、不回退）—— 按钮那时是禁用的，
 *    但把这条语义写死，将来谁把禁用改掉也不会顺手把列表搞空。
 */
export function browseQueryDecision(
  draft: BrowseFilterState,
  applied: BrowseFilterState,
): BrowseQueryDecision {
  const allowed = isFilterActive(draft);
  return {
    allowed,
    next: allowed ? { ...draft } : { ...applied },
    changed: allowed && !sameBrowseFilter(draft, applied),
  };
}

// ---------------------------------------------------------------- 界面固定文案

/**
 * ⛔ **未选时的那张空态引导卡已整块删除**（2026-10-18 本轮，用户圈红说"都不需要"）。
 *    所以这里**没有** `describeBrowseNeedsSelection()` / `quickBrowseChips()` 了 ——
 *    没有界面消费的文案函数留着，下一轮很容易又被接回去。
 *    "没选条件"时页面上只剩筛选卡（含灰掉的「查询」按钮），**不要**补提示。
 */

/**
 * 筛选卡里那行**规则**（全页只此一处）。
 *
 * ⚠️ 只讲两件用户用得着的事实：**点「查询」才出结果** / **选择记在本机**。
 *    "不选就不查"**不再解释**了（本轮按用户要求删掉那句禁用原因）：按钮灰着就是那个意思。
 * ⚠️ 「课本要不要先选」不再单独占一行 —— 并进了筛选卡的描述行
 *    （口径不变：**不用**先选，理由见文件头）。
 */
export const BROWSE_FILTER_RULE_HINT =
  '选好条件点「查询」才出结果；选择记在这台设备上（不上传）。';

/** 查询条上两个按钮的字（避免界面与测试各写一份）。 */
export const BROWSE_QUERY_BUTTON_LABEL = '查询';
export const BROWSE_RESET_BUTTON_LABEL = '重置';

/**
 * 「只看多音字」勾选框的**逐字文案**（界面与测试共用一份）。
 *
 * ⚠️ 口径是**人工标注**，不是"拼音库里有几个读音" —— 后者会把变调/古音/生僻音
 *    都算进来（280 个一年级生字会标出 134 个）。
 */
export const POLYPHONIC_FILTER_LABEL = '只看多音字（按你标注的）';

/** 勾选框下面那句说明（讲清"多音字是谁定的"）。 */
export const POLYPHONIC_FILTER_HINT =
  '多音字由管理员在字的行上标注；没标注的都按单音字处理（古音、生僻音不算）。';

// ---------------------------------------------------------------- 本机记忆（localStorage）

/**
 * 本机存储的键名（**版本号在键名里**：将来字段变了，换一个版本号就等于清空旧值，
 * 不会让旧格式被当成新格式读进来）。
 *
 * 键名里带 `scopeKey`（管理员 = `admin`，家长 = 那个孩子的 id）：
 * 同一个浏览器里管理员和家长各记各的，互不干扰 —— 但**逻辑只有一套**。
 *
 * ⚠️ 「先选、再查询」之后，这里存的是**已经生效过的那一套**（点过「查询」的），
 *    **不存**待生效的半截状态：半截状态存下来，下次进来会按一套"从来没查过"的
 *    条件去发请求，行为不可预测（见 `DictationWorkspace` 里那个写回 effect）。
 */
export const BROWSE_FILTER_STORAGE_PREFIX = 'fht.dictation.browse.filters';
export const BROWSE_FILTER_STORAGE_VERSION = 1;

export function browseFilterStorageKey(scopeKey: string): string {
  const scope = scopeKey.trim() === '' ? 'default' : scopeKey.trim();
  return `${BROWSE_FILTER_STORAGE_PREFIX}.v${BROWSE_FILTER_STORAGE_VERSION}.${scope}`;
}

/** 只用到 `getItem` / `setItem` 这两件事（单测里换成一个内存实现）。 */
export interface BrowseFilterStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * 拿 `localStorage`；拿不到（隐私模式 / 服务端渲染 / 被策略禁用）就返回 `null`。
 *
 * ⚠️ 连**取**它都可能抛（某些浏览器在 `file://` 下会），所以整段包在 try 里。
 */
function defaultStorage(): BrowseFilterStorage | null {
  try {
    const storage = (globalThis as unknown as { localStorage?: BrowseFilterStorage }).localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

/** 恢复结果：落到界面上的那份筛选 + 哪几项**失效被丢掉了**（用来提示用户）。 */
export interface BrowseFilterRestoreResult {
  filter: BrowseFilterState;
  dropped: BrowseFilterField[];
}

/**
 * 把存下来的 JSON 变成筛选状态（**逐项校验**，坏值就回落成"全部"）。
 *
 * 判断口径：
 *   · 不是 JSON / 不是对象 / 是数组 → 整份丢掉（当成"没记过"）；
 *   · 字段不存在 → 用默认值，**不算**失效（向前兼容：以后加字段不会让老数据报警）；
 *   · 类型不对（该是字符串却给了数字…）→ 记一个 `dropped`，回落默认；
 *   · 枚举值不认识（年级 / 学期 / 表 / 类别）→ 记一个 `dropped`，回落默认；
 *   · `publisher` / `lessonId` 只校验**类型**：它们存不存在要等列表到手
 *     （见 `reconcileBrowseFilter`）。
 *   · **旧版本留下的 `textbookId` 直接忽略**（2026-10-18：字段换成了 `publisher`）：
 *     读不出来就当没选 —— 不报错、不提示、也不去猜"那本书对应哪个出版社"。
 *     旧的年级 / 学期 / 其它开关**照常保留**（它们是同一套语义，没有变）。
 */
export function parseStoredBrowseFilter(
  raw: string | null | undefined,
): BrowseFilterRestoreResult {
  const fallback: BrowseFilterRestoreResult = {
    filter: { ...EMPTY_BROWSE_FILTER },
    dropped: [],
  };
  if (typeof raw !== 'string' || raw.trim() === '') return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return fallback;

  const source = parsed as Record<string, unknown>;
  const dropped: BrowseFilterField[] = [];

  const takeString = (
    field: 'publisher' | 'gradeCode' | 'semester' | 'lessonId' | 'listType' | 'wordCategory',
  ): string => {
    const value = source[field];
    if (value === undefined) return '';
    if (typeof value !== 'string') {
      dropped.push(field);
      return '';
    }
    return value;
  };

  const takeFlag = (
    field: 'fromTextbookOnly' | 'overriddenOnly' | 'polyphonicOnly',
  ): boolean => {
    const value = source[field];
    if (value === undefined) return false;
    if (typeof value !== 'boolean') {
      dropped.push(field);
      return false;
    }
    return value;
  };

  const publisher = takeString('publisher');
  const lessonId = takeString('lessonId');

  let gradeCode = takeString('gradeCode');
  if (gradeCode !== '' && !(GRADE_CODES as readonly string[]).includes(gradeCode)) {
    dropped.push('gradeCode');
    gradeCode = '';
  }

  let semester = takeString('semester');
  if (semester !== '' && !(SEMESTER_VALUES as readonly string[]).includes(semester)) {
    dropped.push('semester');
    semester = '';
  }

  let listType = takeString('listType');
  if (listType !== '' && !(LIST_TYPE_VALUES as readonly string[]).includes(listType)) {
    dropped.push('listType');
    listType = '';
  }

  let wordCategory = takeString('wordCategory');
  if (
    wordCategory !== '' &&
    !(WORD_CATEGORY_VALUES as readonly string[]).includes(wordCategory)
  ) {
    dropped.push('wordCategory');
    wordCategory = '';
  }

  return {
    filter: {
      publisher,
      gradeCode,
      semester,
      lessonId,
      listType,
      wordCategory,
      fromTextbookOnly: takeFlag('fromTextbookOnly'),
      overriddenOnly: takeFlag('overriddenOnly'),
      polyphonicOnly: takeFlag('polyphonicOnly'),
    },
    dropped,
  };
}

/**
 * 存成 JSON。**字段逐个写出来**（不是 `JSON.stringify(filter)`）：
 * 落盘格式与字段顺序都是稳定的，将来一眼能看出"上次存的是什么"。
 *
 * ⚠️ 2026-10-18：第一格从 `textbookId` 换成了 `publisher`。**键名保持 v1 不变**：
 *    旧数据里的 `textbookId` 会在读取时被忽略（`parseStoredBrowseFilter`），
 *    而年级 / 学期 / 开关照常恢复 —— 换版本号等于把用户记住的那几格也一起清掉。
 */
export function serializeBrowseFilter(filter: BrowseFilterState): string {
  return JSON.stringify({
    publisher: filter.publisher,
    gradeCode: filter.gradeCode,
    semester: filter.semester,
    lessonId: filter.lessonId,
    listType: filter.listType,
    wordCategory: filter.wordCategory,
    fromTextbookOnly: filter.fromTextbookOnly,
    overriddenOnly: filter.overriddenOnly,
    polyphonicOnly: filter.polyphonicOnly,
  });
}

/** 读上次的选择（`storage` 省略 = 本机 `localStorage`；传 `null` = 当作没有存储）。 */
export function readBrowseFilter(
  scopeKey: string,
  storage: BrowseFilterStorage | null = defaultStorage(),
): BrowseFilterRestoreResult {
  if (storage === null) return { filter: { ...EMPTY_BROWSE_FILTER }, dropped: [] };
  let raw: string | null = null;
  try {
    raw = storage.getItem(browseFilterStorageKey(scopeKey));
  } catch {
    return { filter: { ...EMPTY_BROWSE_FILTER }, dropped: [] };
  }
  return parseStoredBrowseFilter(raw);
}

/**
 * 记下这次的选择（**只记已生效的那一套**）。**写不进去就算了**（隐私模式），
 * 返回是否写成功。
 *
 * ⚠️ 调用处传的必须是"点过「查询」"的那套（`DictationWorkspace` 里是 `filter`，
 *    不是 `draft`）：存半截状态会让下次进来按一套没查过的条件发请求。
 * ⚠️ 不抛错：存偏好失败不该让整个列表打不开。
 */
export function writeBrowseFilter(
  scopeKey: string,
  filter: BrowseFilterState,
  storage: BrowseFilterStorage | null = defaultStorage(),
): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(browseFilterStorageKey(scopeKey), serializeBrowseFilter(filter));
    return true;
  } catch {
    return false;
  }
}

/**
 * 恢复后**对照现有数据核对一遍**：`publisher` / `lessonId` 指向的东西还在不在。
 *
 * 传 `null` = **不知道**（列表还没到手 / 没拿到）⇒ 不判失效、原样保留。
 * 这样"课本列表暂时拉不到"不会把用户记住的筛选清掉。
 *
 * 出版社没了时**连 `lessonId` 一起清**：课文是挂在课本下面的，书没了课一定也没了。
 * 返回值里 `filter` 是**同一个引用**（没有失效项时）——调用处可以直接用
 * `result.filter === prev` 判断"什么都没变，别 setState"。
 *
 * ⚠️ 2026-10-18：核对的是**出版社还在不在**（`publishers`，课本列表去重后的那一份），
 *    不再是 `textbookId` —— 那个字段已经不在筛选里了。出版社没了（那家出版社的
 *    书全删了）就回到「全部出版社」，这是同一个"值失效"的落点。
 */
export function reconcileBrowseFilter(
  filter: BrowseFilterState,
  context: { publishers: readonly string[] | null; lessonIds: readonly string[] | null },
): BrowseFilterRestoreResult {
  const dropped: BrowseFilterField[] = [];
  if (
    filter.publisher !== '' &&
    context.publishers !== null &&
    !context.publishers.includes(filter.publisher)
  ) {
    dropped.push('publisher');
  }
  if (
    filter.lessonId !== '' &&
    context.lessonIds !== null &&
    !context.lessonIds.includes(filter.lessonId)
  ) {
    dropped.push('lessonId');
  }
  if (dropped.length === 0) return { filter, dropped: [] };
  return { filter: clearBrowseFilterFields(filter, dropped), dropped };
}

/**
 * 把某几项**回落成默认值**（"那家出版社没有书了"的落点）。
 *
 * ⚠️ 「出版社」失效时**连课文一起清**：课文挂在课本下面，书没了课一定也没了。
 * ⚠️ 没有任何一项可清时返回**同一个引用**（调用处据此判断"什么都没变，别 setState"）。
 *
 * 待生效那套也要跟着清（`DictationWorkspace` 里调的），否则下拉里还留着
 * 一个已经不存在的出版社 —— 看起来"选过了"，却永远查不出东西。
 */
export function clearBrowseFilterFields(
  filter: BrowseFilterState,
  dropped: readonly BrowseFilterField[],
): BrowseFilterState {
  const clearPublisher = dropped.includes('publisher');
  const clearLesson = clearPublisher || dropped.includes('lessonId');
  if (!clearPublisher && !clearLesson) return filter;
  const next: BrowseFilterState = { ...filter };
  if (clearPublisher) next.publisher = '';
  if (clearLesson) next.lessonId = '';
  return next;
}

/**
 * 失效回落的提示语（**中性色**，不是错误态：值没了不是用户做错了什么）。
 *
 * 没有失效项时返回空串（调用处据此不渲染那一行）。
 */
export function describeDroppedFilterValues(dropped: readonly BrowseFilterField[]): string {
  if (dropped.length === 0) return '';
  const labels = dropped.map((field) => `「${FIELD_LABELS[field]}」`).join('、');
  return (
    `上次查询过的条件里，${labels}已经失效了（例如那家出版社的书被删了），已安全地回到「全部」—— ` +
    '重新选一个条件、点「查询」就会列出来。'
  );
}
